import type { PDFDocumentLoadingTask } from 'pdfjs-dist';
import type { PDFViewer } from 'pdfjs-dist/legacy/web/pdf_viewer.mjs';
import React, { memo, useEffect, useRef, useState } from '../../lib/teact/teact';

import { logDebugError } from '../../util/logs';

import useLang from '../../hooks/useLang';

import Button from '../ui/Button';
import Spinner from '../ui/Spinner';

import styles from './MediaViewer.module.scss';

type Props = {
  src: string;
  fileName?: string;
};

export default memo(({ src, fileName }: Props) => {
  const lang = useLang();
  const viewportRef = useRef<HTMLDivElement>();
  const pagesRef = useRef<HTMLDivElement>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [retryRevision, setRetryRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let loadingTask: PDFDocumentLoadingTask | undefined;
    let viewer: PDFViewer | undefined;
    let resizeObserver: ResizeObserver | undefined;
    setStatus('loading');

    const fail = (error: unknown) => {
      if (controller.signal.aborted) return;
      logDebugError('Transaction PDF preview', error);
      setStatus('error');
    };

    void (async () => {
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
      await import('pdfjs-dist/legacy/web/pdf_viewer.css');
      const { PDFViewer: Viewer, EventBus } = await import('pdfjs-dist/legacy/web/pdf_viewer.mjs');
      if (controller.signal.aborted) return;

      const workerUrl = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).href;
      const assetsUrl = new URL('pdfjs/', workerUrl).href;
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      const eventBus = new EventBus();
      const options = {
        container: viewportRef.current!,
        viewer: pagesRef.current!,
        eventBus,
        abortSignal: controller.signal,
        textLayerMode: 0,
        annotationMode: pdfjs.AnnotationMode.DISABLE,
        enableAutoLinking: false,
        maxCanvasPixels: 4 * 1024 * 1024,
      };
      viewer = new Viewer(options);
      eventBus.on('pagesinit', () => {
        if (!controller.signal.aborted) viewer!.currentScaleValue = 'page-width';
      });
      eventBus.on('pagerendered', ({ error }: { error?: Error }) => {
        if (controller.signal.aborted) return;
        if (error) fail(error);
        else setStatus('ready');
      });
      loadingTask = pdfjs.getDocument({
        url: src,
        disableFontFace: true,
        cMapUrl: `${assetsUrl}cmaps/`,
        cMapPacked: true,
        standardFontDataUrl: `${assetsUrl}standard_fonts/`,
        wasmUrl: `${assetsUrl}wasm/`,
      });
      const document = await loadingTask.promise;
      if (controller.signal.aborted) return;
      viewer.setDocument(document);
      resizeObserver = new ResizeObserver(() => {
        if (!controller.signal.aborted && viewer?.pdfDocument) viewer.currentScaleValue = 'page-width';
      });
      resizeObserver.observe(viewportRef.current!);
    })().catch(fail);

    return () => {
      controller.abort();
      resizeObserver?.disconnect();
      viewer?.setDocument(undefined!);
      void loadingTask?.destroy().catch((error) => logDebugError('Transaction PDF cleanup', error));
    };
  }, [retryRevision, src]);

  return (
    <div className={styles.documentPreview}>
      <div
        ref={viewportRef}
        className={styles.pdfViewport}
        role="region"
        aria-label={fileName}
        data-stricterdom-ignore
      >
        <div ref={pagesRef} className="pdfViewer" />
      </div>
      {status === 'loading' && <div className={styles.pdfStatus}><Spinner /></div>}
      {status === 'error' && (
        <div className={styles.pdfStatus}>
          <span>{lang('Something went wrong.')}</span>
          <div className={styles.pdfErrorActions}>
            <Button
              isSimple
              isRound
              ariaLabel={lang('Try Again')}
              onClick={() => setRetryRevision((value) => value + 1)}
            >
              <i className="icon-update" aria-hidden />
            </Button>
            <a href={src} download={fileName} aria-label={lang('Save')} className={styles.actionButton}>
              <i className="icon-download" aria-hidden />
            </a>
          </div>
        </div>
      )}
    </div>
  );
});
